import { Dispatch } from "redux";
import { IDType, LeagueMatchSmallElement } from "../../config/types";
import { defaultGet, fetchError } from "./all";
import {
  MatchLocationEnum,
  SportEnum,
  HomeMatch,
  MatchSmall,
  CancelablePromise,
  ScreenService,
  MatchService,
  MatchType,
  ManOfTheMatchService,
  TeamOfTheWeekLineup,
  MatchBetVoting,
  MatchBetService,
} from "../../client/api";
import { MatchActions, MatchActionTypes } from "./types";
import { numberToDay, mapToMatchSmallSmall } from "../../config/utils";
import client from "../../client";

let pendingMatchDaysRequest: CancelablePromise<string[]> | null = null;
let pendingLeagueMatchDaysRequest: CancelablePromise<string[]> | null = null;

export function fetchMatchDays(
  dispatch: Dispatch<MatchActions>,
  aoi: IDType,
  date: Date,
  type: "today" | "bigger" | "smaller" | "month",
  sports: SportEnum
) {
  dispatch({ type: MatchActionTypes.FETCH_MATCH_DAYS });
  pendingMatchDaysRequest?.cancel?.();

  const request = client().getMatchDays(date, { aoi, type }, sports);

  pendingMatchDaysRequest = request;

  request
    .then((response: string[]) => {
      dispatch({
        type: MatchActionTypes.FETCH_SUCCESS_MATCH_DAYS,
        payload: response,
      });
    })
    .catch((e: unknown) => {
      const err = e as { name?: string; message?: string } | undefined;
      if (err?.name === "CancelError" || err?.message?.includes?.("aborted"))
        return;
      fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch);
    })
    .finally(() => {
      if (pendingMatchDaysRequest === request) pendingMatchDaysRequest = null;
    });
}

export function fetchMatchDaysOfLeague(
  dispatch: Dispatch<MatchActions>,
  league: IDType,
  date: Date,
  type: "today" | "bigger" | "smaller" | "month",
  sports: SportEnum
) {
  dispatch({ type: MatchActionTypes.FETCH_MATCH_DAYS_LEAGUE });

  pendingLeagueMatchDaysRequest?.cancel?.();

  const request = client().getMatchDays(date, { league, type }, sports);

  pendingLeagueMatchDaysRequest = request;

  request
    .then((response: string[]) => {
      dispatch({
        type: MatchActionTypes.FETCH_SUCCESS_MATCH_DAYS_LEAGUE,
        payload: {
          id: league,
          response: response.map((d) => new Date(d)),
          fetchDate: new Date(),
        },
      });
    })
    .catch((e: unknown) => {
      const err = e as { name?: string; message?: string } | undefined;
      if (err?.name === "CancelError" || err?.message?.includes?.("aborted"))
        return;
      fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch);
    })
    .finally(() => {
      if (pendingLeagueMatchDaysRequest === request)
        pendingLeagueMatchDaysRequest = null;
    });
}

let pendingHomeMatchesRequest: CancelablePromise<HomeMatch[]> | null = null;
let pendingLeagueMatchesRequest: CancelablePromise<MatchSmall[]> | null = null;

export function fetchLeagueMatchesOfDay(
  dispatch: Dispatch<MatchActions>,
  aoi: IDType,
  date: number,
  sports: SportEnum
) {
  pendingHomeMatchesRequest?.cancel?.();
  dispatch({ type: MatchActionTypes.FETCH_LEAGUE_MATCHES_DAY });
  dispatch({
    type: MatchActionTypes.SET_LAST_REQUESTED_LEAGUE_MATCHES_KEY,
    payload: { id: aoi, date },
  });
  const reqDate = numberToDay(date);
  const p = client().getLeagueMatchesOfDay(reqDate, aoi, sports);
  pendingHomeMatchesRequest = p;
  p.then(
    (response) => {
      dispatch({
        type: MatchActionTypes.FETCH_SUCCESS_LEAGUE_MATCHES,
        payload: { id: aoi, date, response, fetchDate: new Date() },
      });
    },
    (e) => {
      const err = e as { name?: string; message?: string } | undefined;
      if (err?.name === "CancelError" || err?.message?.includes?.("aborted")) {
        return;
      }
      fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch);
    }
  );
}

export function fetchLeagueMatchesOfDayLeague(
  dispatch: Dispatch<MatchActions>,
  date: number,
  leagueID: IDType
) {
  pendingLeagueMatchesRequest?.cancel?.();
  dispatch({ type: MatchActionTypes.FETCH_MATCHES_OF_LEAGUE });
  dispatch({
    type: MatchActionTypes.SET_LAST_REQUESTED_MATCH_ELEMENTS_KEY,
    payload: { id: leagueID, date },
  });
  const reqDate = numberToDay(date);
  const p = client().getLeagueMatchesOfDayLeague(reqDate, leagueID);
  pendingLeagueMatchesRequest = p;
  p.then(
    (response) => {
      dispatch({
        type: MatchActionTypes.FETCH_SUCCESS_MATCHES_OF_LEAGUE,
        payload: { id: leagueID, date, response, fetchDate: new Date() },
      });
    },
    (e) => {
      const err = e as { name?: string; message?: string } | undefined;
      if (err?.name === "CancelError" || err?.message?.includes?.("aborted")) {
        return;
      }
      fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch);
    }
  );
}

export function fetchMatchesByLeagueAndMatchDay(
  dispatch: Dispatch<MatchActions>,
  leagueId: IDType,
  matchDay: number
) {
  dispatch({ type: MatchActionTypes.FETCH_MATCHES_OF_LEAGUE });
  dispatch({
    type: MatchActionTypes.SET_LAST_REQUESTED_MATCH_ELEMENTS_KEY,
    payload: { id: leagueId, date: matchDay },
  });

  MatchService.getMatchesByLeagueAndMatchDayMatchesV2LeagueLeagueIdMatchDayMatchDayGet(
    leagueId as number,
    matchDay as number
  ).then(
    (response) => {
      dispatch({
        type: MatchActionTypes.FETCH_SUCCESS_MATCHES_OF_LEAGUE,
        payload: {
          id: leagueId,
          date: matchDay,
          response,
          fetchDate: new Date(),
        },
      });
    },
    (e) => fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch)
  );
}

export function fetchMatchesBySubCategoryAndDate(
  dispatch: Dispatch<MatchActions>,
  subCategoryId: IDType,
  areaOfInterestId: IDType,
  dateStr: string
) {
  dispatch({ type: MatchActionTypes.FETCH_MATCHES_OF_LEAGUE });
  dispatch({
    type: MatchActionTypes.SET_LAST_REQUESTED_MATCH_ELEMENTS_KEY,
    payload: { id: subCategoryId, date: -1 },
  });

  MatchService.getMatchesBySubcategoryAndAreaOfInterestId(
    subCategoryId as number,
    dateStr,
    areaOfInterestId as number
  ).then(
    (response) => {
      if (response && response.length > 0) {
        dispatch({
          type: MatchActionTypes.FETCH_SUCCESS_MATCHES_OF_LEAGUE,
          payload: {
            id: subCategoryId,
            date: -1,
            response,
            fetchDate: new Date(),
          },
        });
      }
    },
    (e) => fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch)
  );
}

export function fetchMatchesByLeagueAndDate(
  dispatch: Dispatch<MatchActions>,
  leagueId: IDType,
  dateStr: string
) {
  dispatch({ type: MatchActionTypes.FETCH_MATCHES_OF_LEAGUE });
  dispatch({
    type: MatchActionTypes.SET_LAST_REQUESTED_MATCH_ELEMENTS_KEY,
    payload: { id: leagueId, date: -1 },
  });

  MatchService.getMatchesByLeagueAndDateMatchesV2LeagueLeagueIdDateMatchDateGet(
    leagueId as number,
    dateStr
  ).then(
    (response) => {
      if (response) {
        dispatch({
          type: MatchActionTypes.FETCH_SUCCESS_MATCHES_OF_LEAGUE,
          payload: {
            id: leagueId,
            date: -1,
            response,
            fetchDate: new Date(),
          },
        });
      }
    },
    (e) => fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch)
  );
}

export function getLineupOfMatch(
  dispatch: Dispatch<MatchActions>,
  matchID: IDType,
  team: MatchLocationEnum
) {
  defaultGet(
    dispatch,
    MatchActionTypes.FETCH_LINEUP_SUCCESS,
    MatchActionTypes.FETCH_ERROR,
    MatchActionTypes.FETCH_LINEUP,
    client().getLineup,
    false,
    { id: matchID, date: team === "home" ? 1 : 2 },
    matchID,
    team
  );
}

export function fetchTotw(
  dispatch: Dispatch<MatchActions>,
  leagueId: IDType,
  matchDay: number
) {
  dispatch({ type: MatchActionTypes.FETCH_TOTW });
  ManOfTheMatchService.getTeamOfTheWeekManOfTheMatchTeamLeagueIdMatchdayMatchDayGet(
    leagueId as number,
    matchDay as number
  ).then(
    (response: TeamOfTheWeekLineup | null) => {
      dispatch({
        type: MatchActionTypes.FETCH_TOTW_SUCCESS,
        payload: {
          id: leagueId,
          date: matchDay,
          response,
          fetchDate: new Date(),
        },
      });
    },
    (e) => fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch)
  );
}

export function fetchMatchBetVoting(
  dispatch: Dispatch<MatchActions>,
  matchId: IDType
) {
  dispatch({ type: MatchActionTypes.FETCH_MATCH_BET });
  MatchBetService.getMatchBetVotingByMatchMatchBetMatchIdGet(matchId as number).then(
    (response: MatchBetVoting) => {
      dispatch({
        type: MatchActionTypes.FETCH_MATCH_BET_SUCCESS,
        payload: {
          id: matchId,
          response,
          fetchDate: new Date(),
        },
      });
    },
    (e) => fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch)
  );
}

export function voteMatchBet(
  dispatch: Dispatch<MatchActions>,
  matchId: IDType,
  bet: MatchLocationEnum
) {
  dispatch({ type: MatchActionTypes.FETCH_MATCH_BET });
  MatchBetService.voteMatchMatchBetPut({ matchId: matchId as number, bet }).then(
    (response: MatchBetVoting) => {
      dispatch({
        type: MatchActionTypes.FETCH_MATCH_BET_SUCCESS,
        payload: {
          id: matchId,
          response,
          fetchDate: new Date(),
        },
      });
    },
    (e) => fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch)
  );
}

export function fetchMatchScreen(
  dispatch: Dispatch<MatchActions>,
  matchId: IDType
) {
  dispatch({ type: MatchActionTypes.FETCH_MATCH_SCREEN });
  ScreenService.getMatchScreenScreenMatchMatchIdGet(Number(matchId)).then(
    (response) => {
      dispatch({
        type: MatchActionTypes.FETCH_SUCCESS_MATCH_SCREEN,
        payload: { id: matchId, response, fetchDate: new Date() },
      });
    },
    (e) => fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch)
  );
}

export function fetchTeamMatches(
  dispatch: Dispatch<MatchActions>,
  teamId: IDType
) {
  dispatch({ type: MatchActionTypes.FETCH_TEAM_MATCHES });
  MatchService.getMatchesByTeamMatchesV2TeamTeamIdGet(Number(teamId)).then(
    (response) => {
      const teamMatchesGroup: LeagueMatchSmallElement[] = [
        {
          leagueTournamentId: Number(teamId),
          leagueTournamentName: {
            textDE: `Team ${teamId} Spiele`,
            textIT: `Partite Team ${teamId}`,
            textEN: `Team ${teamId} Matches`,
            id: Number(teamId),
          },
          leagueTournamentType: MatchType.LEAGUE,
          matches: response.map(mapToMatchSmallSmall),
          teamId: Number(teamId),
        },
      ];

      dispatch({
        type: MatchActionTypes.FETCH_SUCCESS_TEAM_MATCHES,
        payload: {
          id: teamId,
          response: teamMatchesGroup,
          fetchDate: new Date(),
        },
      });
    },
    (e) => fetchError(e, MatchActionTypes.FETCH_ERROR, dispatch)
  );
}
